import React, { useState, useCallback } from 'react';
import stylesPassword from './PasswordPage.module.scss';
import stylesModal from '../../components/ModalWrapper/ModalSlideShowWrapper.module.scss';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import PageTitle from '../PageTitle/PageTitle';
import { captureException } from '@sentry/minimal';

const styles = { ...stylesModal, ...stylesPassword };

type Props = {
  match: any;
  location: any;
  onSubmit?: (state?: any) => void;
};

export const PASSWORD_REGEXP = new RegExp(
  '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})'
);

const PasswordPage: React.FC<Props> = ({ onSubmit = () => {} }) => {
  const { t } = useTranslation('i18n');
  const [submitted, setSubmitted] = useState(false);

  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>('');

  const validateState = useCallback(() => {
    const validation: {
      newPassword?: string;
      newPasswordConfirm?: string;
    } = {};

    if (newPassword !== newPasswordConfirm) {
      validation.newPasswordConfirm = t('pages.user.passwordDoNotMatch');
    }

    if (!PASSWORD_REGEXP.test(newPassword)) {
      validation.newPassword = t('pages.user.passwordDoNotMatchRules');
    }

    return validation;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword, newPasswordConfirm]);

  const validation = validateState();

  const handleSubmit = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitted(true);
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      return;
    }

    if (Object.keys(validation).length === 0) {
      Auth.currentAuthenticatedUser()
        .then((user) => {
          return Auth.changePassword(user, oldPassword, newPassword);
        })
        .then(() => {
          toast.success(t('pages.user.paswordUpdated.success'));
          onSubmit();
        })
        .catch((err) => {
          captureException(err);
          console.log(err);
          toast.error(t('pages.user.passwordInvalid'));
        });
    }
  };

  return (
    <div className={styles.page}>
      <PageTitle title={t(`pages.user.changePassword`)} />
      <div className={styles.content}>
        <Form onSubmit={handleSubmit}>
          {JSON.stringify(validateState)}
          <Container className={styles.root}>
            <Row>
              <Col sm={6}>
                <Form.Group controlId='oldPassword'>
                  <Form.Label>{t('pages.user.oldPassword')} *</Form.Label>
                  <Form.Control
                    required
                    type='password'
                    placeholder={t('pages.user.oldPassword')}
                    value={oldPassword}
                    onChange={(event) => setOldPassword(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <Row>
              <Col sm={6}>
                <Form.Group controlId='newPassword'>
                  <Form.Label>{t('pages.user.newPassword')} *</Form.Label>
                  <Form.Control
                    required
                    type='password'
                    placeholder={t('pages.user.newPassword')}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                </Form.Group>
                {submitted && validation.newPassword && (
                  <p className='text-danger'>{validation.newPassword}</p>
                )}
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group controlId='newPasswordConfirm'>
                  <Form.Label>
                    {t('pages.user.newPasswordConfirm')} *
                  </Form.Label>
                  <Form.Control
                    required
                    type='password'
                    placeholder={t('pages.user.newPasswordConfirm')}
                    value={newPasswordConfirm}
                    onChange={(event) =>
                      setNewPasswordConfirm(event.target.value)
                    }
                    isInvalid={submitted && newPassword !== newPasswordConfirm}
                  />
                </Form.Group>
                {submitted && validation.newPasswordConfirm && (
                  <p className='text-danger'>{validation.newPasswordConfirm}</p>
                )}
              </Col>
            </Row>
            <hr />
            <Row>
              <Col sm={12} className={styles.buttons}>
                <Button
                  variant='secondary'
                  className={styles.right}
                  onClick={() => onSubmit()}
                >
                  {t('common.action.close')}
                </Button>
                <Button type='submit' variant='primary' className={styles.left}>
                  {t('common.action.save')}
                </Button>
              </Col>
            </Row>
          </Container>
        </Form>
      </div>
    </div>
  );
};

export default withRouter(PasswordPage);
